import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Select from "react-select";
import {
  LuAlertCircle,
  LuKeyRound,
  LuListFilter,
  LuMail,
  LuPhone,
  LuUser,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditUser,
  getIsUserModalOpen,
  toggleUserModal,
} from "../../features/users/usersSlice";
import { iconSelectStyles } from "../../static/styles";
import { IFormInput, OptionsProps } from "../../types/users";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formFieldIconClass,
  formSelectIconClass,
} from "../ui/styles";

type Props = {
  handleSaveUser: (data: IFormInput) => void;
};

const UserFormModal = ({ handleSaveUser }: Props) => {
  const dispatch = useAppDispatch();
  const editUser = useAppSelector(getEditUser);
  const isUserModalOpen = useAppSelector(getIsUserModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "સક્રિય",
    },
    {
      value: 0,
      label: "નિષ્ક્રિય",
    },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { name: "", email: "", phone: "", password: "", status: 1 },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveUser(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleUserModal(false));
  };

  useEffect(() => {
    if (editUser && editUser.id) {
      reset({
        name: editUser.name,
        email: editUser.email,
        phone: editUser.phone,
        password: "",
        status: editUser.status,
      });
    } else {
      reset({ name: "", email: "", phone: "", password: "", status: 1 });
    }
  }, [editUser, isUserModalOpen, reset]);

  return (
    <Dialog open={isUserModalOpen === true} onClose={handleCloseFormModal}>
      <DialogContent
        size="md"
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>યુઝર ની માહિતી</h2>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                નામ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.name && "error")}>
                <LuUser className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="યુઝરનુ નામ દાખલ કરો"
                  className={fieldInputClass(!!errors.name)}
                  {...register("name", { required: true })}
                />
                {errors.name && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.name && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  યુઝરનુ નામ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                ઇમેઇલ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.email && "error")}>
                <LuMail className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="યુઝરનુ ઇમેઇલ દાખલ કરો"
                  className={fieldInputClass(!!errors.email)}
                  {...register("email", { required: true })}
                />
                {errors.email && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.email && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  યુઝરનુ ઇમેઇલ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                ફોન નં. <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.phone && "error")}>
                <LuPhone className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="યુઝરનુ ફોન નં. દાખલ કરો"
                  className={fieldInputClass(!!errors.phone)}
                  {...register("phone", { required: true })}
                />
                {errors.phone && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.phone && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  યુઝરનુ ફોન નં. દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                પાસવર્ડ
                {editUser && editUser.id ? null : (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <div className={cx("relative", errors.password && "error")}>
                <LuKeyRound className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="યુઝરનુ પાસવર્ડ દાખલ કરો"
                  className={fieldInputClass(!!errors.password)}
                  {...register("password", {
                    required: editUser && editUser.id ? false : true,
                  })}
                />
                {errors.password && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.password && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  યુઝરનુ પાસવર્ડ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                સ્ટેટ્સ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.status && "error")}>
                <LuListFilter className={formSelectIconClass} />
                <Controller
                  name="status"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      value={
                        statusOption.find((val) => val.value === field.value) ||
                        null
                      }
                      options={statusOption}
                      placeholder="સ્ટેટ્સ સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.status && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(val: any) =>
                        field.onChange(val ? val.value : null)
                      }
                    />
                  )}
                />
              </div>
              {errors.status && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  સ્ટેટ્સ સિલેક્ટ કરો
                </p>
              )}
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              રદ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  સાચવી રહ્યા છીએ...
                </span>
              ) : (
                "સાચવો"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserFormModal;
